import { formatoDecimal } from "../utilities/formatoNumeros";

const PIPELINE_MONITOR_STORAGE_KEY = "liquidacionPipelineMonitorRegistry";
const SNAPSHOT_TTL_MS = 1000 * 60 * 60 * 24 * 7;
const LIVE_SNAPSHOT_TTL_MS = 1000 * 60 * 45;

const PIPELINE_COPY = {
    standard: {
        eyebrow: "Liquidacion estandar",
        insumos: "Validacion de ordenes y novedades aptas para liquidar.",
        estructura: "Persistencia del encabezado y el cuerpo de la liquidacion.",
        fiscal: "Calculo de retenciones y percepciones via Portal, API intermedia y Calyx.",
        comprobante: "Preparacion o facturacion del comprobante segun el modo seleccionado.",
        confirmacion: "Confirmacion final y cierre de la liquidacion."
    },
    devolucion: {
        eyebrow: "Liquidacion por devolucion",
        insumos: "Validacion de ordenes, devoluciones y novedades asociadas al circuito.",
        estructura: "Persistencia del encabezado y el cuerpo de la devolucion de liquidacion.",
        fiscal: "Calculo tributario del circuito de devolucion via Portal, API intermedia y Calyx.",
        comprobante: "Preparacion o facturacion del comprobante de devolucion segun el modo elegido.",
        confirmacion: "Confirmacion final y cierre del circuito de devolucion."
    }
};

const OVERALL_STATUS_RANK = {
    running: 0,
    failed: 1,
    paused: 2,
    ready: 3,
    pending: 4,
    success: 5
};

export const PIPELINE_STATUS_LABELS = {
    running: 'En ejecucion',
    paused: 'En pausa',
    failed: 'Con error',
    success: 'Finalizado',
    ready: 'En gestion',
    pending: 'Pendiente',
    blocked: 'Bloqueado'
};

export const PIPELINE_STEP_STATUS_LABELS = {
    pending: 'Pendiente',
    ready: 'Listo',
    running: 'En proceso',
    paused: 'Pausado',
    success: 'Exitoso',
    failed: 'Con error',
    blocked: 'Bloqueado'
};

export const normalizePipelineStepProgress = (step = {}) => {
    if (Number.isFinite(step?.progress)) {
        return Math.max(0, Math.min(100, Math.round(step.progress)));
    }

    if (step?.status === 'success') return 100;
    if (step?.status === 'running') return 55;
    if (step?.status === 'ready') return 5;
    if (step?.status === 'failed') return 100;
    if (step?.status === 'blocked') return 0;
    return 0;
};

export const getPipelineDisplaySummary = (pipeline = {}) => {
    const steps = Array.isArray(pipeline.steps) ? pipeline.steps : [];
    const completedCount = steps.filter((step) => step?.status === 'success').length;
    const totalCount = steps.length;
    const overallProgress = totalCount > 0
        ? Math.round(steps.reduce((acc, step) => acc + normalizePipelineStepProgress(step), 0) / totalCount)
        : 0;

    return {
        completedCount,
        totalCount,
        overallProgress,
        statusLabel: PIPELINE_STATUS_LABELS[pipeline.overallStatus] || PIPELINE_STATUS_LABELS.pending
    };
};

const safeJsonParse = (value, fallbackValue) => {
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallbackValue;
    }
};

const readStorageMap = () => {
    if (typeof window === "undefined") {
        return {};
    }

    const rawValue = window.localStorage.getItem(PIPELINE_MONITOR_STORAGE_KEY);
    if (!rawValue) {
        return {};
    }

    const parsedValue = safeJsonParse(rawValue, {});
    return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
};

const writeStorageMap = (value) => {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(PIPELINE_MONITOR_STORAGE_KEY, JSON.stringify(value || {}));
};

const getNumericValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return 0;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
    }

    const normalizedValue = String(value).replace(/\./g, "").replace(",", ".");
    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getCollectionCount = (countValue, listValue) => {
    const parsedCount = Number.parseInt(countValue, 10);
    if (Number.isFinite(parsedCount)) {
        return parsedCount;
    }

    return Array.isArray(listValue) ? listValue.length : 0;
};

const formatMoney = (value) => {
    return `$${formatoDecimal(getNumericValue(value)) || "0,00"}`;
};

const sanitizeRuntimeMap = (stepRuntime = {}) => {
    if (!stepRuntime || typeof stepRuntime !== "object") {
        return {};
    }

    return Object.entries(stepRuntime).reduce((acc, [stepKey, stepValue]) => {
        if (!stepValue || typeof stepValue !== "object") {
            return acc;
        }

        acc[stepKey] = {
            status: stepValue.status || "pending",
            progress: Number.isFinite(stepValue.progress) ? stepValue.progress : undefined,
            note: stepValue.note || "",
            error: stepValue.error || "",
            meta: Array.isArray(stepValue.meta) ? stepValue.meta.filter(Boolean) : undefined
        };

        return acc;
    }, {});
};

const sanitizePipelineSnapshot = (snapshot) => {
    if (!snapshot || typeof snapshot !== "object") {
        return null;
    }

    const updatedAt = snapshot.updatedAt || "";
    const updatedAtTime = updatedAt ? new Date(updatedAt).getTime() : 0;

    if (!updatedAtTime || Number.isNaN(updatedAtTime)) {
        return null;
    }

    if (Date.now() - updatedAtTime > SNAPSHOT_TTL_MS) {
        return null;
    }

    const isFreshLiveSnapshot = Date.now() - updatedAtTime <= LIVE_SNAPSHOT_TTL_MS;
    const stepRuntime = sanitizeRuntimeMap(snapshot.stepRuntime);
    const freshRuntime = isFreshLiveSnapshot
        ? stepRuntime
        : Object.entries(stepRuntime).reduce((acc, [stepKey, stepValue]) => {
            if (stepValue.status === "success") {
                acc[stepKey] = stepValue;
            }
            return acc;
        }, {});

    return {
        idLiquidacion: snapshot.idLiquidacion,
        idSeller: snapshot.idSeller,
        flowKind: snapshot.flowKind === "devolucion" ? "devolucion" : "standard",
        executionMode: snapshot.executionMode === "automatico" ? "automatico" : "manual",
        isRunning: isFreshLiveSnapshot ? Boolean(snapshot.isRunning) : false,
        isPaused: isFreshLiveSnapshot ? Boolean(snapshot.isPaused) : false,
        errorMessage: isFreshLiveSnapshot ? (snapshot.errorMessage || "") : "",
        failedStepKey: isFreshLiveSnapshot ? (snapshot.failedStepKey || "") : "",
        currentStepKey: isFreshLiveSnapshot ? (snapshot.currentStepKey || "") : "",
        resumeStepIndex: Number.isFinite(snapshot.resumeStepIndex) ? snapshot.resumeStepIndex : 0,
        updatedAt,
        stale: !isFreshLiveSnapshot,
        stepRuntime: freshRuntime
    };
};

const applyRuntimeToStep = (runtimeMap, stepKey, baseStep) => {
    const runtimeStep = runtimeMap?.[stepKey];

    if (!runtimeStep) {
        return baseStep;
    }

    return {
        ...baseStep,
        ...runtimeStep,
        meta: runtimeStep.meta || baseStep.meta
    };
};

export const persistLiquidacionPipelineSnapshot = (snapshot) => {
    if (!snapshot?.idLiquidacion) {
        return;
    }

    const storageMap = readStorageMap();
    storageMap[snapshot.idLiquidacion] = {
        ...snapshot,
        updatedAt: new Date().toISOString()
    };
    writeStorageMap(storageMap);
};

export const clearLiquidacionPipelineSnapshot = (idLiquidacion) => {
    if (!idLiquidacion) {
        return;
    }

    const storageMap = readStorageMap();
    delete storageMap[idLiquidacion];
    writeStorageMap(storageMap);
};

export const getLiquidacionPipelineSnapshot = (idLiquidacion) => {
    if (!idLiquidacion) {
        return null;
    }

    const storageMap = readStorageMap();
    const snapshot = sanitizePipelineSnapshot(storageMap[idLiquidacion]);

    if (!snapshot && storageMap[idLiquidacion]) {
        delete storageMap[idLiquidacion];
        writeStorageMap(storageMap);
    }

    return snapshot;
};

export const buildLiquidacionPipelineMonitorModel = (liquidacion = {}, snapshot = null) => {
    const safeSnapshot = sanitizePipelineSnapshot(snapshot);
    const flowKind = safeSnapshot?.flowKind || "standard";
    const executionMode = safeSnapshot?.executionMode || "manual";
    const copy = PIPELINE_COPY[flowKind] || PIPELINE_COPY.standard;
    const runtimeMap = safeSnapshot?.stepRuntime || {};
    const ordenCount = getCollectionCount(liquidacion?.cantidadOrdenes, liquidacion?.cuerpoOrdenLiquidaciones);
    const novedadCount = getCollectionCount(liquidacion?.cantidadNovedades, liquidacion?.cuerpoNovedadLiquidaciones);
    const totalRetenciones = getNumericValue(liquidacion?.totalRetenciones);
    const totalPercepcion = getNumericValue(liquidacion?.totalPercepcion);
    const isConfirmada = Number(liquidacion?.idTipoEstadoLiquidacion) === 3;
    const hasDraft = Boolean(liquidacion?.idLiquidacion);
    const hasFiscalData = isConfirmada || totalRetenciones > 0 || totalPercepcion > 0;
    const hasInvoiceData = Boolean(
        liquidacion?.numeroComprobante ||
        liquidacion?.descripcionTipoFactura ||
        liquidacion?.codigoSap ||
        liquidacion?.idTipoFactura
    );

    const steps = [
        applyRuntimeToStep(runtimeMap, "insumos", {
            key: "insumos",
            label: "Ordenes",
            description: copy.insumos,
            status: ordenCount > 0 ? "success" : "pending",
            progress: ordenCount > 0 ? 100 : 0,
            meta: [
                `${ordenCount} ordenes listas`,
                `${novedadCount} novedades`,
                `${ordenCount + novedadCount} registros`
            ],
            note: ordenCount > 0
                ? "La liquidacion ya tiene ordenes aptas para continuar."
                : "Todavia no hay ordenes listas para avanzar."
        }),
        applyRuntimeToStep(runtimeMap, "estructura", {
            key: "estructura",
            label: "Encabezado y cuerpo",
            description: copy.estructura,
            status: hasDraft ? "success" : "pending",
            progress: hasDraft ? 100 : 0,
            meta: [
                `Liquidacion #${liquidacion?.idLiquidacion || "-"}`,
                liquidacion?.razonSocialSeller || "Seller no identificado"
            ],
            note: hasDraft
                ? "El borrador ya fue generado y persistido."
                : "Pendiente de generar encabezado y cuerpo."
        }),
        applyRuntimeToStep(runtimeMap, "fiscal", {
            key: "fiscal",
            label: "Calculo fiscal",
            description: copy.fiscal,
            status: hasFiscalData ? "success" : (ordenCount > 0 ? "ready" : "pending"),
            progress: hasFiscalData ? 100 : 0,
            meta: [
                `Retenciones: ${formatMoney(totalRetenciones)}`,
                `Percepciones: ${formatMoney(totalPercepcion)}`,
                "Portal -> API intermedia -> Calyx"
            ],
            note: hasFiscalData
                ? "La liquidacion ya tiene resultados fiscales persistidos."
                : "Pendiente de calculo tributario."
        }),
        applyRuntimeToStep(runtimeMap, "comprobante", {
            key: "comprobante",
            label: executionMode === "automatico" ? "Facturacion automatica" : "Preparacion de comprobante",
            description: copy.comprobante,
            status: isConfirmada || (executionMode === "manual" && hasInvoiceData)
                ? "success"
                : (hasFiscalData ? "ready" : "pending"),
            progress: isConfirmada || (executionMode === "manual" && hasInvoiceData) ? 100 : 0,
            meta: hasInvoiceData
                ? [
                    liquidacion?.numeroComprobante ? `Comprobante ${liquidacion.numeroComprobante}` : "Comprobante cargado",
                    liquidacion?.descripcionTipoFactura || "Tipo de factura configurado",
                    liquidacion?.codigoSap ? `SAP ${liquidacion.codigoSap}` : "SAP pendiente"
                ]
                : [
                    executionMode === "automatico" ? "Modo automatico habilitado" : "Carga manual requerida",
                    "Sin comprobante confirmado"
                ],
            note: hasFiscalData
                ? "La etapa ya puede continuar con la emision del comprobante."
                : "Esta etapa depende del calculo fiscal."
        }),
        applyRuntimeToStep(runtimeMap, "confirmacion", {
            key: "confirmacion",
            label: "Confirmacion",
            description: copy.confirmacion,
            status: isConfirmada
                ? "success"
                : (executionMode === "automatico"
                    ? (hasFiscalData ? "ready" : "pending")
                    : (hasFiscalData && hasInvoiceData ? "ready" : "pending")),
            progress: isConfirmada ? 100 : 0,
            meta: [
                isConfirmada ? "Liquidacion confirmada" : "Pendiente de cierre",
                liquidacion?.mostrarFecha || "Sin fecha visible"
            ],
            note: isConfirmada
                ? "El circuito ya quedo cerrado."
                : "La confirmacion queda disponible al completar las validaciones previas."
        })
    ];

    const overallStatus = safeSnapshot?.errorMessage
        ? "failed"
        : safeSnapshot?.isRunning
            ? "running"
            : safeSnapshot?.isPaused
                ? "paused"
                : isConfirmada
                    ? "success"
                    : hasFiscalData || hasInvoiceData || ordenCount > 0
                        ? "ready"
                        : "pending";

    const updatedAtLabel = safeSnapshot?.updatedAt
        ? new Date(safeSnapshot.updatedAt).toLocaleString("es-AR")
        : "";

    const footerNote = safeSnapshot?.errorMessage
        ? "Abra el pipeline detallado para revisar el error y relanzar la etapa fallida."
        : safeSnapshot?.isRunning
            ? `Pipeline operativo en sesion. Ultima actualizacion: ${updatedAtLabel}`
            : safeSnapshot?.isPaused
                ? `Pipeline pausado en sesion. Ultima actualizacion: ${updatedAtLabel}`
                : safeSnapshot?.stale
                    ? "Se muestra el estado persistido de la liquidacion. El snapshot operativo de sesion ya expiro."
                    : isConfirmada
                        ? "Circuito finalizado y persistido."
                        : "Abra el pipeline detallado para continuar, pausar o ejecutar acciones manuales.";

    return {
        idLiquidacion: liquidacion?.idLiquidacion,
        idSeller: liquidacion?.idSeller,
        title: `Liquidacion #${liquidacion?.idLiquidacion || "-"}`,
        subtitle: [liquidacion?.razonSocialSeller, liquidacion?.mostrarFecha].filter(Boolean).join(" | "),
        eyebrow: copy.eyebrow,
        flowKind,
        executionMode,
        steps,
        isRunning: Boolean(safeSnapshot?.isRunning),
        isPaused: Boolean(safeSnapshot?.isPaused),
        errorMessage: safeSnapshot?.errorMessage || "",
        failedStepKey: safeSnapshot?.failedStepKey || "",
        overallStatus,
        monitorGroup: overallStatus === "success" ? "finalizados" : (overallStatus === "failed" ? "errores" : "activos"),
        updatedAtLabel,
        footerNote,
        isConfirmada,
        statusRank: OVERALL_STATUS_RANK[overallStatus] ?? OVERALL_STATUS_RANK.pending
    };
};
